/// <reference types="vite-plugin-pwa/client" />
// Aplicativo instalável (PWA): registro do service worker gerado pelo vite-plugin-pwa
// (Workbox; configuração em vite.config.ts, visão geral em docs/ARQUITETURA.md).
//
// Atualização ("autoUpdate"): o navegador confere o sw.js a cada carregamento, e o site
// confere de novo a cada hora e ao voltar para a tela. A versão nova assume sozinha, mas a
// página aberta não é recarregada no meio do uso (o que fecharia diálogos e perderia a
// posição na página): o próximo clique num link interno carrega a página inteira, já nela.
import { registerSW } from 'virtual:pwa-register'

const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000
/** Ao voltar para a aba ou para o app depois deste tempo, confere se há versão nova. */
const UPDATE_CHECK_ON_RETURN_MS = 10 * 60 * 1000
/** Evita recarregar em sequência se a parte que falhou continuar indisponível. */
const RELOAD_GUARD_KEY = 'poderaberto:recarga-apos-atualizacao'
const RELOAD_GUARD_MS = 30_000

/** Uma versão nova do site já controla esta página. */
let updateReady = false

export function setupPwa(): void {
  if (!import.meta.env.PROD) return
  window.addEventListener('vite:preloadError', reloadAfterMissingChunk)
  if (!('serviceWorker' in navigator)) return
  window.addEventListener('click', loadNextPageFully, { capture: true })
  registerSW({
    onRegisteredSW: (_url, registration) => {
      if (registration) checkForUpdates(registration)
    },
    // Chamado no lugar do recarregamento automático do plugin.
    onNeedReload: () => {
      updateReady = true
    },
    onRegisterError: () => {
      // Sem service worker (navegação privada, bloqueio): o site funciona igual, só sem a
      // cópia local.
    },
  })
}

function checkForUpdates(registration: ServiceWorkerRegistration): void {
  let lastCheck = Date.now()
  const check = () => {
    if (registration.installing || !navigator.onLine) return
    lastCheck = Date.now()
    registration.update().catch(() => {
      // Sem conexão ou servidor fora do ar: confere de novo mais tarde.
    })
  }
  window.setInterval(() => {
    if (document.visibilityState === 'visible') check()
  }, UPDATE_CHECK_INTERVAL_MS)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && Date.now() - lastCheck >= UPDATE_CHECK_ON_RETURN_MS) check()
  })
}

/**
 * Com uma versão nova já ativa, o próximo clique num link interno não passa pelo React
 * Router: o navegador segue o link e carrega a página inteira, já na versão nova.
 */
function loadNextPageFully(event: MouseEvent): void {
  if (!updateReady || event.defaultPrevented || event.button !== 0) return
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
  const link = event.target instanceof Element ? event.target.closest('a[href]') : null
  if (!(link instanceof HTMLAnchorElement) || link.hasAttribute('download')) return
  if (link.target && link.target !== '_self') return
  const url = new URL(link.href)
  const here = window.location
  if (url.origin !== here.origin) return
  // Âncora na mesma página (ex.: "Pular para o conteúdo"): segue no React.
  if (url.pathname === here.pathname && url.search === here.search) return
  event.stopPropagation()
}

/**
 * Uma parte do site carregada sob demanda não existe mais no servidor: houve deploy depois
 * que esta aba abriu. Recarrega uma vez, já no endereço pedido; se não resolver, fica a
 * tela de erro (RouteErrorPage), que explica e oferece recarregar.
 */
function reloadAfterMissingChunk(): void {
  if (!navigator.onLine || !claimReload()) return
  // Depois que o roteador registrar o endereço novo no histórico.
  window.setTimeout(() => window.location.reload(), 0)
}

function claimReload(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY))
    if (last && Date.now() - last < RELOAD_GUARD_MS) return false
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()))
    return true
  } catch {
    // Sem como evitar recargas em sequência: fica a tela de erro.
    return false
  }
}
